import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, 
  Sparkles
} from 'lucide-react';

// Animated background component
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 right-20 w-96 h-96 bg-gold/20 rounded-full blur-3xl"
        animate={{
          x: [0, 100, 0],
          y: [0, -50, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -50, 0],
          y: [0, 100, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

// Animated stat card
const StatCard = ({ number, label, sublabel, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.05 }}
      className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-2xl p-6 border border-gold/20 text-center flex flex-col justify-between h-full min-h-[180px]"
    >
      <motion.div 
        className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400 mb-4"
        animate={{ 
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        }}
        transition={{ duration: 3, repeat: Infinity }}
        style={{ backgroundSize: '200% 200%' }}
      >
        {number}
      </motion.div>
      <div className="mt-auto">
        <div className="text-white text-sm uppercase tracking-wider font-medium">{label}</div>
        <div className="text-white text-sm uppercase tracking-wider font-medium mt-1">{sublabel}</div>
      </div>
    </motion.div>
  );
};

export default function HomePage() {
  return (
    <div className="min-h-screen bg-black text-white overflow-hidden flex items-center justify-center">
      {/* Hero Section Only - Full Screen */}
      <section className="relative w-full min-h-screen flex items-center justify-center">
        <AnimatedBackground />
        
        {/* Hero content */}
        <div className="container mx-auto px-4 relative z-10 text-center pt-24">
          {/* Main heading with staggered animation */}
          <motion.div className="mb-4">
            <motion.h1 
              className="text-5xl md:text-7xl lg:text-8xl font-bold"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="block text-white leading-normal">
                Turn PMO Pain
              </span>
              <motion.span 
                className="block text-white mt-1 leading-normal"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Into <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold animate-gradient">PERFORMANCE</span><span className="text-gold">.</span>
              </motion.span>
            </motion.h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Partnering with organizations to solve their most pressing project, program, portfolio, and PMO pain points.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                asChild 
                size="lg" 
                className="bg-gradient-to-r from-gold to-amber-400 hover:from-amber-400 hover:to-gold text-black px-10 py-7 text-lg rounded-full shadow-2xl hover:shadow-gold/50 transition-all font-bold group"
              >
                <Link to="/services" className="flex items-center gap-3">
                  Explore Services
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                asChild 
                size="lg" 
                className="bg-gradient-to-r from-gold to-amber-400 hover:from-amber-400 hover:to-gold text-black px-10 py-7 text-lg rounded-full shadow-2xl hover:shadow-gold/50 transition-all font-bold group"
              >
                <a href="https://cal.com/noah.mccloud/optigolddiscoverycall" className="flex items-center gap-3">
                  Schedule a Discovery Call
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </motion.div>
          </motion.div>

          {/* Section Title */}
          <motion.h2 
            className="text-2xl md:text-3xl font-bold text-white mb-6 mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            Why Businesses Trust OptiGold PMO
          </motion.h2>

          {/* Animated stats - Updated with new message */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto items-stretch"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <StatCard 
              number="$600M+" 
              label="Project & Portfolio" 
              sublabel="DELIVERY" 
              delay={1.3} 
            />
            <StatCard 
              number="20+ Years" 
              label="Industry" 
              sublabel="Experience"
              delay={1.4} 
            />
            <StatCard 
              number="$500M - $37B" 
              label="Enterprise Scale" 
              sublabel="Served"
              delay={1.5} 
            />
            <StatCard 
              number="$0.00" 
              label="BIG FIRM" 
              sublabel="OVERHEAD"
              delay={1.6} 
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
